import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
class Participant extends Base implements EntityType {
    participantId: uuid;
    contactId: uuid;
    relationships: uuid[];
    description?: string | null;
    constructor(doc: typeof Participant | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            participantId: genUUID(),
            contactId: null,
            relationships: [],
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "participant";
        // @ts-ignore
        this.participantId = doc._participantId || doc.participantId || doc.participantid || genUUID();
        // @ts-ignore
        this.contactId = doc._contactId || doc.contactId || doc.contactid || null;
        // @ts-ignore
        this.relationships = doc._relationships || doc.relationships || [];
        // @ts-ignore
        this.description = doc._description || doc.description || null;
    }
    get id(): uuid { return this.participantId; }
    addRelationship(relationship: uuid): uuid {
        if (!relationship) throw new ValidationError('No nulls allowed in an array!');
        if (!this.relationships) this.relationships = [];
        this.relationships.push(relationship);
        return relationship;
    }
    updateRelationship(relationship: uuid) {
        if (!this.relationships) this.relationships = [];
        const index = this.relationships.findIndex(item => item === relationship);
        if (index !== -1) { this.relationships[index] = relationship; }
        else this.addRelationship(relationship);
    }
    removeRelationship(relationship: uuid) {
        if (!this.relationships) return;
        let foundItems = this.relationships?.filter(item => item === relationship);
        if (foundItems && foundItems.length > 0) {
            this.relationships = this.relationships.filter(item => !(item === relationship));
        }
    }
    abandonRelationships() { this.relationships = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this participant"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.participantId; }
    static override getNonRefArrayFields(): string[] { return ["relationships"]; }
    static override getDefaultEntries(): Participant[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'participant',
    dataArea: 'Cases',
    attachedTo: [
        {parents: ['contact', 'lead', 'matter'], via: 'entity', onDelete: 'delete', onReplace: 'move'},
        {parents: ['contact'], via: 'contactId', onDelete: 'block', onReplace: 'move'},
    ],
    table: 'PARTICIPANT',
    comment: 'A participant record links a contact to an owning entity (a matter, lead, or contact) and describes their role via one or more relationship labels (e.g. Client, Spouse, Brother-in-Law). The Client relationship is sacrosanct and is used throughout the app to identify who the firm represents; familial relationships carry reciprocal-opposite metadata so that both sides of a relationship can be recorded automatically.',
    fields: {
        participantId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this participant'
        },

        /**
         * The participant in the matter.
         */
        contactId: {
            type: 'uuid', entityType: 'contact', required: true, comment: 'The participant'
        },

        /**
         * A lookup for relationships, like 'Brother-in-Law'.
         */
        relationships: {
            type: 'uuid[]',
            entityType: 'lookup',
            lookupPaths: ['any:participant:relationship'],
            additionalData: {
                type: {type: 'string', name: 'Type', default: 'Familial'},
                opposite: {type: 'array', name: 'Opposite', default: []}
            },
            required: true,
            comment: 'The relationships of this participant to the matter',
        },

        /**
         * A short description of the participant, or a note on their involvement.
         */
        description: {
            type: 'string', comment: 'A short description of the participant, or a note on their involvement'
        }
    },
    indexes: [
        {name: 'benefactors', fn: t => t.relationships.includes('73d54d07-2016-4a9e-9c89-ad843d93e178')},
    ],
    maps: [
        {name: 'entityId', keysFn: t => t.entityId, presentFn: (t, k) => t.entityId === k},
        {name: 'contactId', keysFn: t => t.contactId, presentFn: (t, k) => t.contactId === k},
        {name: 'relationships', keysFn: t => t.relationships, presentFn: (t, k) => t.relationships?.includes(k)},
    ],
    /**
     * OWA-886: a Benefactor (73d54d07) is emailed invoices, which carry privileged billing
     * narrative, so no participant may also be Opposing Party (53eb1870), Opposing Counsel
     * (167b6b1b), Opposing Counsel's Paralegal (2f9c4d7b) or Opposing Counsel's Legal
     * Assistant (6507b60e) (OWA-180, decision q-20261010-194321-5l6q). Court roles stay neutral.
     * No status is exempt: restoring a deleted version writes it back as active.
     * Deleting stays possible because the DAO's remove() does not validate.
     * client/src/ui/LookupSelect/LookupRegistry.ts ADVERSE_RELATIONSHIPS holds the same IDs.
     */
    invariants: [{
        message: 'A participant cannot be a Benefactor and an Opposing Party, Opposing Counsel or Opposing Counsel\'s staff at the same time. Remove one of these roles.',
        fn: t => !t.relationships?.includes('73d54d07-2016-4a9e-9c89-ad843d93e178')
                 || !t.relationships.some(r => [
                     '53eb1870-58b4-4f4f-a592-9672d940420b', // Opposing Party
                     '167b6b1b-9155-4b3c-8063-9a31d8c6c6b3', // Opposing Counsel
                     '2f9c4d7b-5eaa-4591-add6-b4da1159af74', // Opposing Counsel's Paralegal
                     '6507b60e-3005-43b4-8301-b16cca9140f4'  // Opposing Counsel's Legal Assistant
                 ].includes(r))
    }]
});
    }
}
export default Participant;
